import * as React from 'react'
import {
  UWPNotifier,
  OutputAction,
  DownloadStatus,
  StatusMessage,
  AllDownloadsPayload,
  DownloadStatusPayload
} from '../UWPNotifier'
import {
  LicenseModal,
  LicenseModalRef
} from './LicenseModal'
import {
  triggerPlayback
} from '../triggers'
import {
  DownloadButtonWithLabel,
  DownloadButton,
} from './DownloadButton'
import {
  DownloadStatusMessages
} from './DownloadStatusMessages'
import {
  count
} from '../../../widgets/DVWebNodeSPA/utils/csm'
import { isShowPlayButton, isShowModalBeforePlay } from '../../../widgets/DVWebNodeSPA/utils/downloads'
import { Modal } from '@amzn/dvui/dist/Modal'
import {
  withSpaContext,
  SpaContextProps,
} from '../SpaContext'
import { fillValues } from 'DVWebNodeUtils'
import * as st from './DownloadButton.module.scss'
import history from '../../../widgets/DVWebNodeSPA/history'
import cn from 'classnames'

export type DownloadActionProps = {
  downloadInfo?: {
    downloadURL: string
    titleID: string
    episodeNumber: undefined | number
  }
  withLabel?: boolean
  isErrored?: boolean
}

export type DownloadActionWithSpaContextProps = SpaContextProps & DownloadActionProps

type DownloadActionState = {
  downloadStatus: DownloadStatus
  downloadProgress: number
  downloadStatusMessages: StatusMessage[]
  downloadId?: string
  downloadActionOpen: boolean
  showModalBeforePlay: boolean
  downloadStatusPayload: DownloadStatusPayload
}

const getDownloadStatus = (titleID: string, downloads: AllDownloadsPayload) => {
  return titleID && downloads && downloads[titleID]
}

const getDownloadId = (props: DownloadActionWithSpaContextProps) => {
  return props.downloads && props.downloadInfo && props.downloads[props.downloadInfo.titleID]
  && props.downloads[props.downloadInfo.titleID].DownloadId
}

const getDownloadStateFromProps = (props: DownloadActionWithSpaContextProps, state?: DownloadActionState) => {
  const downloadStatus = props.downloadInfo && getDownloadStatus(props.downloadInfo.titleID, props.downloads)

  return {
    downloadStatus: (downloadStatus && DownloadStatus[downloadStatus.Status]) || DownloadStatus.IDLE,
    downloadProgress: downloadStatus && downloadStatus.Progress || 0,
    downloadStatusMessages: downloadStatus && downloadStatus.StatusMessages,
    downloadId: downloadStatus && props.downloads[props.downloadInfo.titleID].DownloadId,
    downloadActionOpen: state ? state.downloadActionOpen : false,
    downloadStatusPayload: downloadStatus,
    showModalBeforePlay: downloadStatus && isShowModalBeforePlay(downloadStatus)
  }
}

export class DownloadActionWithSpaContext extends React.Component<
  DownloadActionWithSpaContextProps,
  DownloadActionState
> {
  modal = React.createRef<Modal>();
  lock:boolean = false

  constructor(props: DownloadActionWithSpaContextProps) {
    super(props)
    this.state = getDownloadStateFromProps(props)
  }

  startDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    const { downloadInfo: {titleID}, saveOfflineData} = this.props

    this.setState({
      downloadStatus: DownloadStatus.WAITING,
      downloadId: getDownloadId(this.props),
    }, () => {
      UWPNotifier.getInstance().publish(OutputAction.Download_Start, {
      titleId: titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
    })

    saveOfflineData(titleID)
  }

  pauseDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Pause, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
    this.onClick()
  }

  resumeDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Resume, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
    this.onClick()
  }

  deleteDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    
    UWPNotifier.getInstance().publish(OutputAction.Download_Delete, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
  }

  cancelDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Cancel, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
  }

  retryDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Retry, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId })
    })
  }

  playDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    if(this.state.showModalBeforePlay && !navigator.onLine) {
      LicenseModalRef.current.open()
    } else {
      triggerPlayback(this.props.downloadInfo.titleID, this.state.downloadId)
    }
    this.onClick()
  }

  viewDetail = (e: React.MouseEvent) => {
    e.preventDefault()
    if(this.props.downloadInfo && this.props.downloadInfo.titleID){
      history.push(`/detail/${this.props.downloadInfo.titleID}`)
      this.onClick()
    }
  }

  viewError = (e: React.MouseEvent) => {
    e.preventDefault()
    this.modal.current.open()
    this.onClick()
  }

  noActionDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    count('no-action')
  }

  getDownloadAction = () => {
    const { strings } = this.props
    // To be done at a later stage: const startDownloadLink = { text: strings.DV_WINAPP_DROPLIST_STARTDL, onClick: this.startDownload, className: st.startDownload }
    const cancelDownloadLink = { text: strings.DV_WINAPP_DROPLIST_CANCELDL, onClick: this.cancelDownload, className: st.cancelDownload }
    const pauseDownloadLink = { text: strings.DV_WINAPP_DROPLIST_PAUSEDL, onClick: this.pauseDownload, className: st.pauseDownload }
    const viewDetailsLink = { text: strings.DV_WINAPP_DROPLIST_VIEWDETAIL, onClick: this.viewDetail, className: st.viewDetails }
    const resumeDownloadLink = { text: strings.DV_WINAPP_DROPLIST_RESUMEDL, onClick: this.resumeDownload, className: st.resumeDownload }
    const retryDownloadLink = { text: strings.DV_WINAPP_DROPLIST_RETRYDL, onClick: this.retryDownload, className: st.retryDownload }
    const deleteDownloadLink = { text: strings.DV_WINAPP_DROPLIST_DELETEDL, onClick: this.deleteDownload, className: st.retryDownload }
    const playDownloadLink = { text: strings.DV_WINAPP_DROPLIST_PLAY, onClick: this.playDownload, className: st.playDownload }
    const viewErrorLink = { text: strings.DV_WINAPP_DROPLIST_ERROR, onClick: this.viewError, className: st.viewError }

    const showPlayButton = this.state.downloadStatus === DownloadStatus.COMPLETED ? isShowPlayButton(this.state.downloadStatusPayload) : false

    switch (this.state.downloadStatus) {
      case DownloadStatus.IDLE:
        return { action: this.startDownload, links: [] }
      case DownloadStatus.WAITING:
        return { action: this.noActionDownload, links: [] }
      case DownloadStatus.QUEUED:
        return { action: this.noActionDownload, links: [cancelDownloadLink, viewDetailsLink]}
      case DownloadStatus.DOWNLOADING:
        return { action: this.pauseDownload, links: [pauseDownloadLink, cancelDownloadLink, viewDetailsLink]}
      case DownloadStatus.PAUSED:
        return { action: this.resumeDownload, links: [resumeDownloadLink, cancelDownloadLink, viewDetailsLink]}
      case DownloadStatus.ERROR:
        return { action: this.retryDownload, links: [retryDownloadLink, cancelDownloadLink, viewDetailsLink, viewErrorLink]}
      case DownloadStatus.COMPLETED:
        return { action: this.deleteDownload, links: (showPlayButton ? [playDownloadLink] : []).concat([deleteDownloadLink, viewDetailsLink]) }
      case DownloadStatus.DISABLED:
        return { action: this.deleteDownload, links: [deleteDownloadLink, viewDetailsLink] }
      default:
        return { action: this.noActionDownload, links: [] }
    }
  }

  componentDidUpdate(prevProps: DownloadActionWithSpaContextProps) {
    const prevDownloadState = getDownloadStatus(prevProps.downloadInfo.titleID, prevProps.downloads)
    const prevStatus = prevDownloadState && prevDownloadState.Status
    const prevProgress = prevDownloadState && prevDownloadState.Progress

    const newDownloadState = getDownloadStatus(this.props.downloadInfo.titleID, this.props.downloads)
    const newStatus = newDownloadState && newDownloadState.Status
    const newProgress = newDownloadState && newDownloadState.Progress

    if (prevStatus !== newStatus || prevProgress !== newProgress) {
      const downloadState = getDownloadStateFromProps(this.props, this.state)
      this.setState({
        ...downloadState,
      })
    }
  }

  onClick = (e?: any) => {
    if(e) {
      e.preventDefault();
    }
    // We lock to prevent double-clicking
    if(!this.lock) {
      this.lock = true
      this.setState({
        downloadActionOpen: !this.state.downloadActionOpen
      }, () => {
        this.lock = false
      })
    }
  }

  shouldDropListHaveLabel = () => {
    switch (this.state.downloadStatus) {
      case DownloadStatus.QUEUED:
      case DownloadStatus.DOWNLOADING:
      case DownloadStatus.PAUSED:
      case DownloadStatus.ERROR:
        return true
      case DownloadStatus.IDLE:
      case DownloadStatus.WAITING:
      case DownloadStatus.COMPLETED:
        return false
      default:
        return false
    }
  }

  render() {
    const { downloadInfo, withLabel, strings, isErrored } = this.props

    if (!downloadInfo) {
      return null
    }

    const isEpisode = typeof downloadInfo.episodeNumber !== 'undefined'
    const episodeText = downloadInfo.episodeNumber === 0 ? strings.DV_WINAPP_EPISODE_BONUS : fillValues(strings.DV_WINAPP_EPISODE_NB, {episodeNumber: String(downloadInfo.episodeNumber)})

    const downloadAction = this.getDownloadAction()
    const hasDropList = Boolean(downloadAction && downloadAction.links.length)
    const dropListWithLabel = hasDropList && this.shouldDropListHaveLabel()

    const labelAction = {
      IDLE: strings.DV_WINAPP_ACTION_IDLE,
      WAITING: strings.DV_WINAPP_ACTION_WAITING,
      QUEUED: strings.DV_WINAPP_ACTION_QUEUED,
      DOWNLOADING: strings.DV_WINAPP_ACTION_DOWNLOADING,
      PAUSED: strings.DV_WINAPP_ACTION_PAUSED,
      ERROR: strings.DV_WINAPP_ACTION_ERROR,
      COMPLETED: strings.DV_WINAPP_ACTION_COMPLETED,
      REMOVED: strings.DV_WINAPP_ACTION_REMOVED,
      CANCELLED: strings.DV_WINAPP_ACTION_CANCELLED,
      DISABLED: ''
    }

    const labelActionForEpisodes = {
      IDLE: fillValues(strings.DV_WINAPP_ACTION_IDLE_EPISODES, {episodeText}),
      WAITING: strings.DV_WINAPP_ACTION_WAITING,
      QUEUED: fillValues(strings.DV_WINAPP_ACTION_QUEUED),
      DOWNLOADING: fillValues(strings.DV_WINAPP_ACTION_DOWNLOADING_EPISODES, {episodeText}),
      PAUSED: fillValues(strings.DV_WINAPP_ACTION_PAUSED_EPISODES, {episodeText}),
      ERROR: fillValues(strings.DV_WINAPP_ACTION_ERROR_EPISODES, {episodeText}),
      COMPLETED: fillValues(strings.DV_WINAPP_ACTION_COMPLETED_EPISODES, {episodeText}),
      REMOVED: fillValues(strings.DV_WINAPP_ACTION_REMOVED_EPISODES, {episodeText}),
      CANCELLED: fillValues(strings.DV_WINAPP_ACTION_CANCELLED_EPISODES, {episodeText}),
      DISABLED: ''
    }

    const label = isEpisode ? labelActionForEpisodes[this.state.downloadStatus] : labelAction[this.state.downloadStatus]

    const { downloadURL } = downloadInfo

    const downloadButtonProps = {
      label,
      downloadURL,
      status: this.state.downloadStatus,
      progress: this.state.downloadProgress,
      onClick: downloadAction.action
    }

    // Detail Page ATF Button
    const CardDownloadButton = () => ( withLabel ? (
      <DownloadButtonWithLabel
        {...downloadButtonProps}
        forceDoubleHeight={true}
      />
    ) : (
      (!hasDropList) ? // Detail Page BTF/Download page buttons
        <DownloadButton {...downloadButtonProps} /> :
        <>
        <div onClick={this.onClick} className={cn(st.downloadAction, {
          [st.downloadActionOpen]: this.state.downloadActionOpen
        })}>
            {dropListWithLabel && <span className={cn(st.dropListLabel, {
              [st.error]: isErrored
            })}>{label}</span>}
            <DownloadButton {...downloadButtonProps} isDropList={true} />
            <div className={st.downloadActionsButtons}>
              {downloadAction.links.map((link, i) => (
                <div className={st.downloadActionsButton} key={`${this.state.downloadId || ''}-${i}-${link.text || ''}`}>
                  <span onClick={link.onClick} className={cn(st.downloadActionButtonTrigger, link.className)}>{link.text}</span>
                </div>
              ))}
            </div>
        </div>
        <div className={st.downloadActionOverlay} onClick={this.onClick} />
        </>
    ))
    return (
      <div className="avu-full-width-phablet" data-automation-id="download-button">
        <CardDownloadButton />
        {Boolean(this.state.downloadStatusMessages && this.state.downloadStatusMessages.length) && <Modal modalId={`modal-error-${downloadInfo.titleID}`} ref={this.modal} footer={""} header={strings.DV_WINAPP_DOWNLOAD_ERROR}>
          <DownloadStatusMessages messages={this.state.downloadStatusMessages} strings={strings} />
        </Modal>}
        {this.state.showModalBeforePlay && <LicenseModal strings={strings} downloadStatus={this.state.downloadStatusPayload} />}
      </div>
    )
  }
}

export const DownloadAction = withSpaContext(DownloadActionWithSpaContext)
